import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, TimePicker } from "@progress/kendo-react-dateinputs";
import { format } from "date-fns";
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import "../../../../../src/components/Tasks/Reminders/reminder.scss";
import { setCommonGridReminders } from "../../../../actions/reminders";
import { ReminderContext } from "../../../../components/Tasks/Reminders/ReminderContextProvider";
import { addReminder } from "../../../../components/Tasks/Reminders/reminder.service";
import { button, icon, label, notifyIcon, number, quote } from "../../../../config";
import { formatTime, parseTimeStringToDate } from "../../../../helper/common";
import { useCombinationKeys } from "../../../../helper/commonHooks";
import store from "../../../../store";
import { dateConvertUTC } from "../../../../utils";
import { getNotification } from "../../../../utils/common";

export const useReminders = (setShowReminderPopup) => {
  const [selectedDates, setSelectedDates] = useState([]);
  const {
    daysAfterChecked,
    setDaysAfterChecked,
    daysAfter,
    setDaysAfter,
    reminderMessage,
    setReminderMessage,
    setIsReminderMessageEmpty,
    isReminderMessageEmpty,
  } = useContext(ReminderContext);
  const { UserReminderTime } = useSelector((state) => state.auth.user);
  const { commonGridReminders } = useSelector((state) => state.reminders);
  const today = new Date();
  // Minimum datetime is 1 hour from now - prevents selecting past times
  const minDateTime = new Date(today.getTime() + 60 * 60 * 1000);
  const formattedTime = formatTime(UserReminderTime, "HH:mm");
  let initialTime = parseTimeStringToDate(formattedTime);
  
  // State for "Choose date & time" checkbox (replaces Tomorrow)
  const [chooseDateTimeChecked, setChooseDateTimeChecked] = useState(true);
  
  // Calculate default date (tomorrow)
  const getDefaultDate = () => {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  };
  
  // State for date in "Choose date" section
  const [selectedDate, setSelectedDate] = useState(getDefaultDate());
  
  // Single shared time state for all reminder options
  const [selectedTime, setSelectedTime] = useState(initialTime);
  
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const ctrlS = useCombinationKeys("s");
  const titleRef = useRef(null);
  const timePickerPopupHostRef = useRef(null);

  /**
   * Adjusts time when today is selected based on business rules (all in user's profile timezone):
   * - Default time comes from user profile (UserReminderTime).
   * - If selected date is today (in user TZ) and current time (in user TZ) is after default, set to 1 hour ahead of current (user TZ).
   * - If after 11 PM (user TZ) and default has passed, set to 11:59 PM (user TZ).
   * - Prevents setting a reminder in the past.
   * @author Sarthak Arora
   */
  useEffect(() => {
    if (!chooseDateTimeChecked || !selectedDate) return;
    const userTimezone = store.getState()?.auth?.user?.timezone || "UTC";
    const nowInUserTz = utcToZonedTime(new Date(), userTimezone);
    const todayStrUserTz = formatInTimeZone(new Date(), userTimezone, "yyyy-MM-dd");
    const selectedDateStrUserTz = formatInTimeZone(selectedDate, userTimezone, "yyyy-MM-dd");
    const isTodayInUserTz = todayStrUserTz === selectedDateStrUserTz;

    const defaultTime = parseTimeStringToDate(formattedTime);
    const defaultHour = defaultTime.getHours();
    const defaultMinute = defaultTime.getMinutes();

    if (isTodayInUserTz) {
      const currentHour = nowInUserTz.getHours();
      const currentMinute = nowInUserTz.getMinutes();
      const currentMins = currentHour * 60 + currentMinute;
      const defaultMins = defaultHour * 60 + defaultMinute;

      if (currentMins >= defaultMins) {
        if (currentHour >= 23) {
          setSelectedTime(new Date(2000, 0, 1, 23, 59, 0, 0));
        } else {
          const oneHourAheadHour = currentHour + 1;
          const oneHourAheadMin = currentMinute;
          setSelectedTime(new Date(2000, 0, 1, oneHourAheadHour, oneHourAheadMin, 0, 0));
        }
      } else {
        setSelectedTime(new Date(2000, 0, 1, defaultHour, defaultMinute, 0, 0));
      }
    } else {
      setSelectedTime(new Date(2000, 0, 1, defaultHour, defaultMinute, 0, 0));
    }
  }, [selectedDate, chooseDateTimeChecked, formattedTime]);

  /**
   * sets focus on title after one second
   * @author Sarthak Arora
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      handleFocus();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  /**
   * initialise IsReminderMessageEmpty
   * @author Sarthak Arora
   */
  useEffect(() => {
    setIsReminderMessageEmpty(!!!reminderMessage);
  }, []);

  /**
   * set the selected Date based on which checkbox is checked
   * @author Sarthak Arora
   */
  useEffect(() => {
    if (chooseDateTimeChecked) {
      // Combine selectedDate and selectedTime
      const combinedDateTime = new Date(selectedDate);
      combinedDateTime.setHours(selectedTime.getHours(), selectedTime.getMinutes(), 0, 0);
      setSelectedDates([combinedDateTime, ...selectedDates]);
    } else if (daysAfterChecked) {
      setSelectedDates([getFutureDate(daysAfter), ...selectedDates]);
    } else setSelectedDates([new Date()]);
  }, [chooseDateTimeChecked, daysAfterChecked, daysAfter, selectedDate, selectedTime]);

  /**
   * triggers save on CTRL + S
   * @author Sarthak Arora
   */
  useEffect(() => {
    ctrlS && handleSaveReminder();
  }, [ctrlS]);

  /**
   * gives the date object for a future date
   * @param {*} days
   * @returns
   * @author Sarthak Arora
   */
  const getFutureDate = (days) => {
    today.setDate(today.getDate() + days);
    return today;
  };

  /**
   * toggles "Choose date & time" checkbox state
   * @author Sarthak Arora
   */
  const handleChooseDateTimeChecked = () => {
    setChooseDateTimeChecked(!chooseDateTimeChecked);
  };

  /**
   * toggles daysAfter checkbox state
   * @author Sarthak Arora
   */
  const handleDaysAfterChecked = () => {
    setDaysAfterChecked(!daysAfterChecked);
  };

  /**
   * handles DatePicker change
   * Time adjustment is handled by useEffect when date changes
   * @author Sejal
   */
  const handleDateChange = (e) => {
    if (e.value) {
      const newDate = new Date(e.value);
      newDate.setHours(0, 0, 0, 0);
      setSelectedDate(newDate);
    }
  };

  /**
   * Handles shared TimePicker change. Validates that selected time is not in the past (using user's profile timezone).
   * For today (in user TZ): at least 1 hour ahead of current time, or if after 11 PM then not before current time.
   * @author Sejal
   */
  const handleTimeChange = (e) => {
    if (!e.value) return;
    const newTime = e.value;
    const userTimezone = store.getState()?.auth?.user?.timezone || "UTC";
    const nowInUserTz = utcToZonedTime(new Date(), userTimezone);
    const todayStrUserTz = formatInTimeZone(new Date(), userTimezone, "yyyy-MM-dd");
    const selectedDateStrUserTz = formatInTimeZone(selectedDate, userTimezone, "yyyy-MM-dd");
    const isTodayInUserTz = todayStrUserTz === selectedDateStrUserTz;

    if (chooseDateTimeChecked && isTodayInUserTz) {
      const currentHour = nowInUserTz.getHours();
      const currentMinutes = nowInUserTz.getMinutes();
      const selectedHour = newTime.getHours();
      const selectedMinutes = newTime.getMinutes();
      const selectedTimeInMinutes = selectedHour * 60 + selectedMinutes;
      const currentTimeInMinutes = currentHour * 60 + currentMinutes;
      const oneHourAheadInMinutes = currentTimeInMinutes + 60;

      if (currentHour >= 23) {
        if (selectedTimeInMinutes >= currentTimeInMinutes) setSelectedTime(newTime);
      } else {
        if (selectedTimeInMinutes >= oneHourAheadInMinutes) setSelectedTime(newTime);
      }
      return;
    }
    setSelectedTime(newTime);
  };

  const removeReminderTimePickerActionTooltips = useCallback(() => {
    requestAnimationFrame(() => {
      const popupHost = timePickerPopupHostRef.current;
      if (!popupHost) return;
      popupHost.querySelectorAll(".k-time-cancel[title], .k-time-accept[title], .k-actions [title]").forEach((el) => {
        el.removeAttribute("title");
      });
    });
  }, []);

  /**
   * sets the daysAfter based on input
   * @author Sarthak Arora
   */
  const handleNumberOfDaysAfter = (e) => {
    if (!e.target.value) {
      setDaysAfter(number.ONE);
      setDaysAfterChecked(false);
    }
    setDaysAfter(e.target.value);
  };

  /**
   *   handles the increment and decrement of number of days for normal reminders (past due date)
   * @param {boolean} increment - Whether increment button is pressed or decrement
   *
   * @author Sarthak Arora
   */

  const handleDaysAfterChange = (increment) => {
    const newDaysAfter = increment ? daysAfter + number.ONE : daysAfter - number.ONE;
    if (newDaysAfter >= number.TWO) setDaysAfter(newDaysAfter);
  };

  /**
   * returns the updated commonGridReminders
   * @param {*} response
   * @returns {Object} updatedCommonGridReminders
   * @author Sarthak Arora
   */
  const getUpdatedCommonGridReminders = ({ Id, ReminderMessage, ReminderTime, ReminderDate, ReminderType, EntityId, IsSeen }) => {
    const newReminder = {
      Id: Id,
      ReminderMessage: ReminderMessage,
      ReminderTime: ReminderTime,
      ReminderDate: ReminderDate,
      ReminderType: ReminderType,
      IsSeen: IsSeen ? IsSeen : 0,
      Status: new Date(ReminderDate) > new Date() ? label.UPCOMING : label.DUE,
    };
    const updatedCommonGridReminders = [...commonGridReminders, newReminder];
    return updatedCommonGridReminders;
  };

  /**
   * Creates an alert notification action.
   * @param {Date} reminderDate - Date of the reminder being created
   * @param {string} reminderMessage - Message of the reminder being created
   * @param {number} reminderType - type of reminder ( 1 for custom/normal , 2 for dynamic , 3 for general)
   * @author Sarthak Arora
   */

  const createGeneralReminder = async (userId, selectedTime, reminderDate, reminderMessage, reminderType, extraParams) => {
    const reminderDateObj = new Date(reminderDate);
    const userTimezone = store.getState()?.auth?.user?.timezone || "UTC";
    const localMoment = new Date(
      reminderDateObj.getFullYear(),
      reminderDateObj.getMonth(),
      reminderDateObj.getDate(),
      selectedTime.getHours(),
      selectedTime.getMinutes(),
      0,
      0
    );
    const utcMoment = zonedTimeToUtc(localMoment, userTimezone);
    const payload = {
      userId: userId,
      reminderMessage: reminderMessage,
      reminderTime: formatInTimeZone(utcMoment, "UTC", "HH:mm"),
      reminderDate: dateConvertUTC(utcMoment),
      reminderId: null,
      reminderType: reminderType,
      ...extraParams,
    };
    const res = await dispatch(addReminder(payload));
    if (!res) {
      getNotification(quote.FAILED_TO_ADD_REMINDER, notifyIcon.ERROR_ICON);
      setShowReminderPopup(false);
      return;
    }
    res.Status = new Date(reminderDate) > new Date() ? label.UPCOMING : label.DUE;
    return res;
  };

  /**
   *   handles the creation of reminder based on the type
   * @author Sarthak Arora
   */

  const handleSaveReminder = useCallback(async () => {
    setShowReminderPopup(false);
    let reminderCount = 0;
    let remindersList = [...commonGridReminders];
    let res;

    if (chooseDateTimeChecked && selectedDate) {
      // Combine date and shared time
      const combinedDateTime = new Date(selectedDate);
      combinedDateTime.setHours(selectedTime.getHours(), selectedTime.getMinutes(), 0, 0);
      res = await createGeneralReminder(user.id, combinedDateTime, combinedDateTime, reminderMessage, number.THREE);
      if (res) {
        remindersList = [...remindersList, res];
        reminderCount++;
      }
    }

    if (daysAfterChecked) {
      const reminderDateAfterToday = new Date(today.getTime() + daysAfter * 24 * 60 * 60 * 1000);
      // Use shared selectedTime from TimePicker
      reminderDateAfterToday.setHours(selectedTime.getHours(), selectedTime.getMinutes(), 0, 0);
      res = await createGeneralReminder(user.id, reminderDateAfterToday, reminderDateAfterToday, reminderMessage, number.THREE);
      if (res) {
        remindersList = [...remindersList, res];
        reminderCount++;
      }
    }

    if (reminderCount) {
      await dispatch(setCommonGridReminders(remindersList));
      getNotification(quote.REMINDER_SET, notifyIcon.SUCCESS_ICON);
    } else getNotification(quote.FAILED_TO_ADD_REMINDER, notifyIcon.ERROR_ICON);
    setDaysAfter(number.TWO);
    window.dispatchEvent(new CustomEvent('reminderPopupClosed'));
  }, [chooseDateTimeChecked, daysAfterChecked, reminderMessage, daysAfter, selectedDate, selectedTime]);

  /**
   *  sets the reminderMessage
   *  @author: Sarthak Arora
   */
  const inputEvent = (event) => {
    setIsReminderMessageEmpty(event.target.value === "");
    setReminderMessage(event.target.value);
  };

  /**
   * handle the cancel btn
   * @author Sarthak Arora
   */
  const handleCancel = () => {
    setShowReminderPopup(false);
  };

  /**
   * focus on the title field
   * @author Sarthak Arora
   */
  const handleFocus = () => {
    if (titleRef.current) {
      titleRef.current.focus();
    }
  };

  /**
   *  restricts the key down to numbers only
   *  @author: Sarthak Arora
   */
  const handleKeyDown = (e) => {
    if (!(e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Backspace" || e.key === "Delete" || e.key === "Tab" || (e.key >= "0" && e.key <= "9"))) {
      e.preventDefault();
    }
  };

  /**
   * main content for the popup
   * @author Sarthak Arora
   */
  const popupBodyContent = () => {
    return (
      <div className='row reminder-popup-content'>
        {/* Title Section */}
        <div className='col-md-12 form-group'>
          <label className='mb-1'>{label.TITLE}</label>
          <textarea
            id='dynamic-reminder-popup-reminder-title'
            className='w-100 form-control'
            rows={2}
            ref={titleRef}
            onChange={inputEvent}
            placeholder='Enter title text.'
            maxLength={120}
            autoFocus
            onFocus={handleFocus}
            style={{ minHeight: '70px', resize: 'vertical', overflow: 'auto' }}
          />
          {isReminderMessageEmpty && (
            <p className='text-red reminder-title-warning m-0 mt-1' style={{ fontSize: '12px' }}>
              {quote.REMINDER_TITLE_EMPTY}
            </p>
          )}
        </div>

        {/* Date Options Row - Three columns: Choose Date, Days from today, At (Time) */}
        {/* Choose Date */}
        <div className='col-md-4 form-group'>
          <label className='d-flex align-items-center mb-1'>
            <input
              id='dynamic-reminder-popup-datetime-checkbox'
              className='mr-2'
              type='checkbox'
              checked={chooseDateTimeChecked}
              onChange={handleChooseDateTimeChecked}
            />
            <span >{label.CHOOSE_DATE}</span>
          </label>
          <div className='d-flex align-items-center'>
          <DatePicker
            size='small'
            className='reminder-date-picker'
            value={selectedDate}
            onChange={handleDateChange}
            disabled={!chooseDateTimeChecked}
            min={today}
            format='dd-MMM-yyyy'
          />
          </div>
        </div>

        {/* Days from today */}
        <div className='col-md-4 form-group'>
          <label className='d-flex align-items-center mb-1'>
            <input
              id='dynamic-reminder-popup-days-after-checkbox'
              className='mr-2'
              type='checkbox'
              checked={daysAfterChecked}
              onChange={handleDaysAfterChecked}
            />
            <span >{label.DAYS_FROM_TODAY}</span>
          </label>
          <div className='d-flex align-items-center'>
            <input
              className='form-control days-from-today-input'
              type='numeric'
              value={daysAfter}
              disabled={!daysAfterChecked}
              onChange={handleNumberOfDaysAfter}
              onKeyDown={handleKeyDown}
              maxLength={number.TWO}
            />
            <div className='dynamic-reminder-popup-inc-dec-btn-group d-flex flex-column ml-1'>
              <button
                id='dynamic-reminder-popup-inc-days-after'
                className='dynamic-reminder-popup-inc-dec-btn d-flex align-items-center justify-content-center border-0 outline-none'
                disabled={!daysAfterChecked}
                onClick={() => handleDaysAfterChange(true)}>
                {icon.ARROW_UP}
              </button>
              <button
                id='dynamic-reminder-popup-dec-days-after'
                className='dynamic-reminder-popup-inc-dec-btn d-flex align-items-center justify-content-center border-0 outline-none'
                disabled={!daysAfterChecked}
                onClick={() => handleDaysAfterChange(false)}>
                {icon.ARROW_DOWN}
              </button>
            </div>
          </div>
        </div>

        {/* At (Time) */}
        <div className='col-md-4 form-group'>
          <label className='d-flex align-items-center mb-1' >
            <span >{label.AT}</span>
          </label>
          <div className='d-flex align-items-center'>
            
          <TimePicker
            size='small'
            className='reminder-time-picker'
            title=''
            value={selectedTime}
            onChange={handleTimeChange}
            popupSettings={{ className: "reminder-timepicker-popup", appendTo: timePickerPopupHostRef.current }}
            onOpen={removeReminderTimePickerActionTooltips}
            disabled={!chooseDateTimeChecked && !daysAfterChecked}
            steps={{ minute: 10 }}
          />
          <div ref={timePickerPopupHostRef} />
        </div>
        </div>

        {/* Action Buttons */}
        <div className='dt-popup-footer text-right w-100'>
          <button
            id='dynamic-reminder-popup-reminder-cancel-btn'
            className='btn btn-ghost mr-2 reminder-confirmation-btn'
            onClick={handleCancel}>
            {button.CANCEL}
          </button>
          <button
            id='dynamic-reminder-popup-reminder-save-btn'
            className='btn btn-primary reminder-confirmation-btn'
            disabled={isReminderMessageEmpty || (!chooseDateTimeChecked && !daysAfterChecked)}
            onClick={handleSaveReminder}>
            {button.SAVE}
          </button>
        </div>
      </div>
    );
  };

  return { getFutureDate, popupBodyContent, handleSaveReminder, handleCancel, handleKeyDown };
};
